import { fetchRecommendProductListApi } from './api';
import {
  initializeOwlCarousel,
  checkIsInitializedOwlCarousel,
  destroyOwlCarousel,
  deviceItemConfig,
} from './layoutEffect';
import {
  deviceType,
  deviceBreakpoint,
  layoutMode,
  pageIdentifyMappingApiPageType,
  pageIdentifyMappingEventTrackingPageType,
  defaultLayoutStyle,
  productListTargetId,
  sectionTargetId,
} from './constants';
import {
  sendRecommendItem,
  sendViewRelatedProducts,
  sendProductImpression,
} from './tracking';

import intersectionObserver from '../../utils/intersectionObserver';
import getInjector from '../../utils/getInjector';
import getAngularScope from '../../utils/getAngularScope';
import { convertProductCurrency } from '../../utils/currency';
import { PAGE_TYPE } from '../../constants/logger';
import { getNgTrackerService } from '../../service/trackerService';
export class LazyLoadRelatedProducts extends HTMLElement {
  constructor() {
    super();
    // attribute affect
    this.productId = null;
    this.productIds = null;
    this.layoutSetting = null;

    this.observer = null;
    this.productObserver = null;
    this.trackerService = null;
    this.mediaQuery = null;
    this.mediaQueryListener = null;
    this.isDragging = false;

    // data storage
    this.productIdsDataMap = new Map();
    this.isSentViewedEventProductIdsSet = new Set();
  }

  connectedCallback() {
    this.productId = this.getAttribute('data-product-id');
    const dataProductIdsStr = this.getAttribute('data-product-ids');
    // in PLP page. The data-product-ids could be empty string, and it will need to display related products
    this.productIds =
      typeof dataProductIdsStr === 'string'
        ? dataProductIdsStr.split(',').filter(Boolean)
        : null;
    if (this.productId || (this.productIds && this.productIds.length)) {
      this.observer = intersectionObserver(
        (entry, observer) => {
          this.initRelatedProducts();
          observer.unobserve(this);
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0,
        },
      );
      this.observer?.observe(this);
    } else {
      // for LE store. liquid will not have ${sectionTargetId}, hide loading and try to related products section
      this.removeLoadingElementAndHide();
      this.hideRelatedProduct();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.productObserver?.disconnect();
    this.mediaQuery?.removeEventListener('change', this.mediaQueryListener);
  }

  initRelatedProducts() {
    const themePreview = new URLSearchParams(window.location.search).get(
      'theme_preview',
    );
    return fetchRecommendProductListApi({
      themePreview,
      productIds: this.productId ? [this.productId] : this.productIds,
      categoryId: this.getAttribute('data-category-id'),
      pageType: getApiPageType({
        dataPageType: this.getAttribute('data-page-type'),
        dataSearchKeyword: this.getAttribute('data-search-keyword'),
      }),
    }).then((response) => {
      const { components, products, setting } = response ?? {};
      if (components && products) {
        // handle api cache and response value as { layout_style: { } }
        const layoutStyle = Object.keys(setting?.layout_style).length
          ? setting.layout_style
          : defaultLayoutStyle;
        if (components?.length) {
          this.render({
            components,
            products,
            layoutStyle,
          });
          return;
        }
      }
      this.hideRelatedProduct();
    });
  }

  removeLoadingElementAndHide() {
    this.style.display = 'none';
    this.replaceChildren();
  }

  hideRelatedProduct() {
    const closestTargetNode = this.closest(`#${sectionTargetId}`);
    if (closestTargetNode) {
      closestTargetNode.style.display = 'none';
    }
  }

  setupProductIdsDataMap(products) {
    products.forEach((product, index) => {
      // use the product id as key to set the related product data
      this.productIdsDataMap.set(product?.id, {
        relatedProduct: product,
        order: index + 1,
      });
    });
  }

  initProductIntersectionObserver() {
    const targetNode = document.getElementById(productListTargetId);

    // clear existing observer
    if (this.productObserver) {
      this.productObserver.disconnect();
      this.productObserver = null;
    }

    this.productObserver = intersectionObserver(
      (entry, observer) => {
        this.trackProductVisible(entry.target, observer);
        observer.unobserve(entry.target);
      },
      {
        threshold: 0.5,
      },
    );

    const productListNode = Array.from(
      targetNode.querySelectorAll('[product-id]'),
    );
    productListNode.forEach((node) => this.productObserver.observe(node));
  }

  initProductClickEvent() {
    const targetNode = document.getElementById(productListTargetId);

    targetNode.addEventListener(
      'click',
      (e) => {
        const productElement = e.target.closest('[product-id]');
        const productId = productElement?.getAttribute('product-id');
        if (
          !productElement ||
          !targetNode.contains(productElement) ||
          this.isDragging
        ) {
          return;
        }

        const { relatedProduct, order } =
          this.productIdsDataMap.get(productId) || {};
        if (relatedProduct && order) {
          sendRecommendItem({
            relatedProductId: relatedProduct.id,
            order,
            recStrategy: relatedProduct.rec_strategy,
            trackerService: this.trackerService,
            layoutSetting: this.layoutSetting,
            basedProductId: this.productId,
            basedProductIds: this.productIds,
            pageType: getEventPageType({
              dataPageType: this.getAttribute('data-page-type'),
              dataSearchKeyword: this.getAttribute('data-search-keyword'),
            }),
          });
        }
      },
      true,
    );
  }

  trackProductVisible(element) {
    const productId = element?.getAttribute('product-id');
    if (!productId || this.isSentViewedEventProductIdsSet.has(productId))
      return;

    const { relatedProduct, order } =
      this.productIdsDataMap.get(productId) || {};
    if (relatedProduct && order) {
      const pageType = getEventPageType({
        dataPageType: this.getAttribute('data-page-type'),
        dataSearchKeyword: this.getAttribute('data-search-keyword'),
      });
      sendViewRelatedProducts({
        relatedProduct,
        order,
        trackerService: this.trackerService,
        layoutSetting: this.layoutSetting,
        basedProductId: this.productId,
        basedProductIds: this.productIds,
        pageType,
      });
      sendProductImpression({
        trackerService: this.trackerService,
        productId: this.productId,
        pageType,
      });
      this.isSentViewedEventProductIdsSet.add(productId);
    }
  }

  async initEvent(products) {
    try {
      this.trackerService = await getNgTrackerService();
    } catch (error) {
      console.error('Failed to get tracker service:', error);
    }
    const targetNode = document.getElementById(productListTargetId);
    initAngularDigest(targetNode);
    convertProductCurrency();
    // tracking event for related products
    this.setupProductIdsDataMap(products);
    this.initProductIntersectionObserver();
    this.initProductClickEvent();
  }

  applyLayoutSetting({
    currentLayoutMode = layoutMode.FULL,
    productListElement,
    device,
  }) {
    const selector = `#${productListTargetId}`;
    const isInstallOwlCarousel = checkIsInitializedOwlCarousel(selector);
    if (isInstallOwlCarousel) {
      destroyOwlCarousel(selector);
    }

    if (
      currentLayoutMode === layoutMode.CIRCULATIVE_HORIZONTAL_SCROLL ||
      currentLayoutMode === layoutMode.HORIZONTAL_SCROLL
    ) {
      const isLoop =
        currentLayoutMode === layoutMode.CIRCULATIVE_HORIZONTAL_SCROLL &&
        productListElement.length >= deviceItemConfig[device];
      initializeOwlCarousel({
        selector,
        isLoop,
        carouselItems: productListElement,
        onDragStateChange: (isDragging) => {
          this.isDragging = isDragging;
        },
      });
      // need to reinstall observer event, bcuz owl-carousel will clone the element and drop observer
      if (isLoop) {
        this.initProductIntersectionObserver();
      }
    }
  }

  initLayoutSetting({ layoutStyle, productListElement }) {
    const mediaQueryList = window.matchMedia(
      `(max-width: ${deviceBreakpoint.MOBILE}px)`,
    );
    const device = mediaQueryList.matches
      ? deviceType.MOBILE
      : deviceType.DESKTOP;
    this.layoutSetting = layoutStyle[device];
    this.applyLayoutSetting({
      currentLayoutMode: this.layoutSetting,
      productListElement: productListElement,
      device,
    });

    // add event listener for media query changes
    const listener = (event) => {
      const device = event.matches ? deviceType.MOBILE : deviceType.DESKTOP;
      this.layoutSetting = layoutStyle[device];
      this.applyLayoutSetting({
        currentLayoutMode: this.layoutSetting,
        productListElement: productListElement,
        device,
      });
    };
    mediaQueryList.addEventListener('change', listener);
    this.mediaQuery = mediaQueryList;
    this.mediaQueryListener = listener;
  }

  render({ components, products, layoutStyle }) {
    const targetNode = document.getElementById(productListTargetId);
    if (targetNode) {
      const container = document.createElement('div');
      container.innerHTML = components.join('');
      const relatedProductsNodes = Array.from(container.children);
      const isPreviewMode = new URLSearchParams(window.location.search).get(
        'pdp_v2026',
      );
      relatedProductsNodes.forEach((node) => {
        // Add preview mode param to related product links
        if (node.tagName === 'A' && isPreviewMode) {
          const href = node.getAttribute('href');
          if (href) {
            const url = new URL(href);
            url.searchParams.set('pdp_v2026', isPreviewMode);
            node.setAttribute('href', url.toString());
          }
        }

        targetNode.appendChild(node);
      });
      this.removeLoadingElementAndHide();
      this.initEvent(products);
      this.initLayoutSetting({
        layoutStyle,
        productListElement: relatedProductsNodes,
      });
    }
  }
}

if (!customElements.get('lazyload-related-products')) {
  customElements.define('lazyload-related-products', LazyLoadRelatedProducts);
}

export const initAngularDigest = async (element) => {
  const injector = await getInjector(PAGE_TYPE.pdp);
  // product-item wishlist need to get scope from MainController
  const $MainControllerScope = await getAngularScope(
    PAGE_TYPE.pdp,
    document.querySelector('[ng-controller="MainController"]'),
  );
  if (injector && $MainControllerScope) {
    const $compile = injector.get('$compile');
    $compile(element)($MainControllerScope);
    $MainControllerScope.$digest();
  }
};

export function getApiPageType({ dataPageType, dataSearchKeyword }) {
  if (dataSearchKeyword) {
    return pageIdentifyMappingApiPageType.search_product;
  }
  return (
    pageIdentifyMappingApiPageType?.[dataPageType] ||
    // support LE store. pageType will be null to response PDP page type
    pageIdentifyMappingApiPageType.product_detail
  );
}

export function getEventPageType({ dataPageType, dataSearchKeyword }) {
  if (dataSearchKeyword) {
    return pageIdentifyMappingEventTrackingPageType.search_product;
  }
  return (
    pageIdentifyMappingEventTrackingPageType?.[dataPageType] ||
    // support LE store. pageType will be null to response PDP page type
    pageIdentifyMappingEventTrackingPageType.product_detail
  );
}
